// The stateful side of Enroll Hosts runs (PCD-9663). See enrollmentRuns.spec.md.
// Loads and saves runs in user preferences, follows role status from resmgr, and sends the
// storage checks one at a time. All decisions live in the pure enrollmentRuns.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import ApiClient from 'api-client/ApiClient'
import { UserPreferences } from 'app/constants'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { rawResmgrHostSelector } from 'openstack/components/infrastructure/selectors'
import type { CheckAnswer, ResmgrHostStatus } from './enrollmentRuns'
import {
  addRun,
  applyCheckAnswer,
  CHECK_TIMEOUT_MS,
  createRun,
  dismissRuns as dismissRunsFrom,
  hostsPending,
  markRunning,
  nextCheck,
  queueChecks,
  retryCheck as retryCheckIn,
  skipRemovedChecks,
  updateRunHosts,
} from './enrollmentRuns'
import type { EnrollmentRun, EnrollmentSelection } from './model'
import type { ResmgrHostLike } from './startRun'
import { applyEnrollment } from './startRun'

export const POLL_INTERVAL_MS = 15000

interface SavedEnrollments {
  runs?: EnrollmentRun[]
}

export interface EnrollmentRunner {
  runs: EnrollmentRun[]
  loaded: boolean
  startRun: (selection: EnrollmentSelection, hosts: ResmgrHostLike[]) => Promise<string>
  retryCheck: (runId: string, serviceHost: string) => void
  dismissRuns: (runIds: string[]) => void
}

// SC-004: a check saved while in flight is sent again after a page load.
const requeueRunning = (runs: EnrollmentRun[]): EnrollmentRun[] =>
  runs.map((run) =>
    run.checks.some((check) => check.state === 'running')
      ? {
          ...run,
          checks: run.checks.map((check) =>
            check.state === 'running' ? { ...check, state: 'queued' } : check,
          ),
        }
      : run,
  )

// ENR-FR-007: a host moved phase, checks were queued or skipped; role-only changes are not saved.
const isTransition = (before: EnrollmentRun, after: EnrollmentRun): boolean =>
  before.checks.length !== after.checks.length ||
  before.checks.some((check, index) => check.state !== after.checks[index].state) ||
  before.hosts.some((host, index) => host.phase !== after.hosts[index].phase)

// ENR-FR-005: a check with no answer in time resolves as timed out, so the queue moves on.
const verifyWithTimeout = (serviceHost: string): Promise<CheckAnswer> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ timedOut: true }), CHECK_TIMEOUT_MS)
    ApiClient.getInstance()
      .cinder.verifyBackend(serviceHost)
      .then(
        (result) => resolve({ result }),
        (error) => resolve({ error }),
      )
      .finally(() => clearTimeout(timer))
  })

const useEnrollmentRunner = (): EnrollmentRunner => {
  const { prefs: rootPrefs } = useScopedPreferences()
  const region = (rootPrefs.currentRegion as string) || ''
  const { prefs, fetchUserDefaults, updateUserDefaults } = useScopedPreferences('defaults')
  const saved = prefs[UserPreferences.HostEnrollments] as SavedEnrollments | null | undefined
  const { reload } = useListAction(listResmgrHosts, { loadOnDemand: true })
  const resmgrHosts = useSelector(rawResmgrHostSelector) as ResmgrHostStatus[]
  const resmgrHostsRef = useRef(resmgrHosts)
  resmgrHostsRef.current = resmgrHosts

  const [allRuns, setAllRuns] = useState<EnrollmentRun[]>([])
  const [fetched, setFetched] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Refs let timers and the async check loop read the latest values without restarting.
  const runsRef = useRef<EnrollmentRun[]>([])
  const regionRef = useRef(region)
  regionRef.current = region
  const mountedRef = useRef(true)
  const inFlightRef = useRef(false)
  const wakeRef = useRef<ReturnType<typeof setTimeout>>()
  const fetchRef = useRef(fetchUserDefaults)
  fetchRef.current = fetchUserDefaults
  const saveRef = useRef(updateUserDefaults)
  saveRef.current = updateUserDefaults
  const reloadRef = useRef(reload)
  reloadRef.current = reload

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearTimeout(wakeRef.current)
    }
  }, [])

  // ENR-FR-007: keep runs in memory, and save every run (all regions) when asked.
  const commit = useCallback((next: EnrollmentRun[], persist: boolean) => {
    runsRef.current = next
    if (mountedRef.current) setAllRuns(next)
    if (persist) saveRef.current(UserPreferences.HostEnrollments, { runs: next }, true)
  }, [])

  // ENR-FR-007: load saved runs once on mount.
  useEffect(() => {
    Promise.resolve(fetchRef.current(UserPreferences.HostEnrollments)).then(() => {
      if (mountedRef.current) setFetched(true)
    })
  }, [])

  useEffect(() => {
    if (!fetched || loaded) return
    commit(requeueRunning(saved?.runs || []), false)
    setLoaded(true)
  }, [fetched, loaded, saved, commit])

  const regionRuns = useMemo(
    () =>
      allRuns
        .filter((run) => run.region === region)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [allRuns, region],
  )
  // Also while checks wait, so a Persistent Storage role removed meanwhile is seen (ENR-FR-009).
  const pending =
    loaded &&
    (hostsPending(regionRuns) ||
      regionRuns.some((run) => run.checks.some((check) => check.state === 'queued')))

  // ENR-FR-002: reload resmgr hosts every 15 s while any host or check of the region is pending.
  useEffect(() => {
    if (!pending) return undefined
    const timer = setInterval(() => reloadRef.current(true, false), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [pending])

  // ENR-FR-002, ENR-FR-003: apply each new resmgr host list to the region's runs.
  useEffect(() => {
    // ponytail: an empty list is treated as "not loaded yet", so a region whose hosts all
    // vanished never marks them gone. Add a loaded flag if that case matters.
    if (!loaded || !resmgrHosts?.length) return
    const now = new Date()
    let changed = false
    let persist = false
    const next = runsRef.current.map((run) => {
      if (run.region !== region) return run
      const updated = updateRunHosts(run, resmgrHosts, now)
      if (updated === run) return run
      changed = true
      persist = persist || isTransition(run, updated)
      return updated
    })
    if (changed) commit(next, persist)
  }, [resmgrHosts, loaded, region, commit])

  // ENR-FR-004: send one check at a time. The effect under it runs it again on every change.
  const pump = useCallback(async () => {
    if (inFlightRef.current || !mountedRef.current) return
    clearTimeout(wakeRef.current)
    const now = new Date()
    const runs = runsRef.current.filter((run) => run.region === regionRef.current)
    const target = nextCheck(runs, now)
    if (!target) {
      // Checks waiting for their retry time: wake up when the earliest one is due.
      const due = runs
        .flatMap((run) => run.checks)
        .filter((check) => check.state === 'queued' && check.notBefore)
        .map((check) => new Date(check.notBefore as string).getTime())
      if (due.length) {
        wakeRef.current = setTimeout(pump, Math.max(0, Math.min(...due) - now.getTime()))
      }
      return
    }
    const forTarget = (update: (run: EnrollmentRun) => EnrollmentRun) =>
      runsRef.current.map((run) => (run.id === target.runId ? update(run) : run))

    inFlightRef.current = true
    commit(
      forTarget((run) => markRunning(run, target.serviceHost)),
      false,
    )
    const answer = await verifyWithTimeout(target.serviceHost)
    inFlightRef.current = false
    // Unmounted: the saved copy still says "queued", so the next mount sends it again.
    if (!mountedRef.current) return
    // A retry queued here is skipped right away if the role was removed meanwhile (ENR-FR-009).
    commit(
      forTarget((run) =>
        skipRemovedChecks(
          applyCheckAnswer(run, target.serviceHost, answer, new Date()),
          resmgrHostsRef.current || [],
        ),
      ),
      true,
    )
    // The effect below sees the new runs and calls pump for the next check.
  }, [commit])

  useEffect(() => {
    if (loaded && regionRuns.length) pump()
  }, [regionRuns, loaded, pump])

  // ENR-FR-001: apply roles, then save a new run. If every host was rejected (or
  // ServicesNotReadyError), throw with nothing saved so the drawer stays on step 5. If only some
  // were rejected, the others are already enrolling, so save the run with those hosts failed.
  const startRun = useCallback(
    async (selection: EnrollmentSelection, hosts: ResmgrHostLike[]): Promise<string> => {
      // Read before the calls: a region switch while they run must not move the run.
      const runRegion = regionRef.current
      const errors = await applyEnrollment(selection, hosts)
      const failed = hosts.filter((host) => errors[host.id])
      if (failed.length === hosts.length && failed.length) {
        throw new Error(
          hosts.length === 1
            ? errors[failed[0].id]
            : failed
                .map((host) => `${host.info?.hostname || host.id}: ${errors[host.id]}`)
                .join('; '),
        )
      }
      const now = new Date()
      const id = uuid()
      const run = createRun({
        id,
        region: runRegion,
        now,
        selection,
        hosts: hosts.map((host) => ({ id: host.id, name: host.info?.hostname || host.id })),
      })
      run.hosts = run.hosts.map((host) =>
        errors[host.hostId] ? { ...host, phase: 'failed', error: errors[host.hostId] } : host,
      )
      commit(addRun(runsRef.current, queueChecks(run, now)), true)
      return id
    },
    [commit],
  )

  // ENR-FR-006: send an ended check again.
  const retryCheck = useCallback(
    (runId: string, serviceHost: string) => {
      const now = new Date()
      commit(
        runsRef.current.map((run) =>
          run.id === runId ? retryCheckIn(run, serviceHost, now) : run,
        ),
        true,
      )
    },
    [commit],
  )

  // ENR-FR-008: drop ended runs.
  const dismissRuns = useCallback(
    (runIds: string[]) => commit(dismissRunsFrom(runsRef.current, runIds), true),
    [commit],
  )

  return { runs: regionRuns, loaded, startRun, retryCheck, dismissRuns }
}

export default useEnrollmentRunner
